import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import parseDxfToMesh, { dxfFacePositions } from 'modules/project/StopeFillPage/Three/parseDxfToMesh';

export const buildMediaUrl = (path: string): string => {
  const value = path.trim();
  if (/^(https?:)?\/\//i.test(value) || value.startsWith('blob:') || value.startsWith('data:')) {
    return value;
  }

  const relativePath = value.replace(/^\/+/, '');
  return relativePath.startsWith('media/') ? `/${relativePath}` : `/media/${relativePath}`;
};

export const CAD_TO_THREEJS_MATRIX = new THREE.Matrix4().set(
  -1, 0, 0, 0,
  0, 0, 1, 0,
  0, 1, 0, 0,
  0, 0, 0, 1,
);

export const parseObjGeometry = (text: string): THREE.BufferGeometry | null => {
  const loader = new OBJLoader();
  const group = loader.parse(text);
  let geometry: THREE.BufferGeometry | null = null;
  group.traverse((child) => {
    if (!geometry && (child as THREE.Mesh).isMesh) {
      geometry = (child as THREE.Mesh).geometry.clone();
    }
  });
  return geometry;
};

export const parseDxfGeometry = (text: string): THREE.BufferGeometry | null => {
  const mesh = parseDxfToMesh(text);
  if (!mesh) return null;
  return mesh.geometry.clone();
};

export const getGeometrySourceExtension = (url: string): string => {
  const path = url.split('?')[0];
  const dotIndex = path.lastIndexOf('.');
  return dotIndex >= 0 ? path.substring(dotIndex).toLowerCase() : '';
};

export interface CadOrigin {
  x: number;
  y: number;
  z: number;
}

export interface LocalGeometry {
  geometry: THREE.BufferGeometry;
  origin: CadOrigin;
}

const ZERO_ORIGIN: CadOrigin = { x: 0, y: 0, z: 0 };
const OBJ_VERTEX = /^v\s+(\S+)\s+(\S+)\s+(\S+)/m;
const OBJ_VERTICES = /^v\s+(\S+)\s+(\S+)\s+(\S+)/gm;

export const firstObjVertex = (text: string): CadOrigin | null => {
  const match = OBJ_VERTEX.exec(text);
  return match ? { x: Number(match[1]), y: Number(match[2]), z: Number(match[3]) } : null;
};

export const translateObjVertices = (text: string, { x, y, z }: CadOrigin): string => text.replace(
  OBJ_VERTICES,
  (_, vx: string, vy: string, vz: string) => `v ${Number(vx) - x} ${Number(vy) - y} ${Number(vz) - z}`,
);

export const firstPoint = (positions: ArrayLike<number>): CadOrigin => (
  positions.length >= 3 ? { x: positions[0], y: positions[1], z: positions[2] } : ZERO_ORIGIN
);

/** Flat [x, y, z, ...] positions relative to `origin`, subtracted in float64 before they are narrowed to float32. */
export const localPositions = (positions: ArrayLike<number>, { x, y, z }: CadOrigin): Float32Array => {
  const origin = [x, y, z];
  return Float32Array.from(positions, (value, index) => value - origin[index % 3]);
};

const parseDxfNearOrigin = (text: string): LocalGeometry | null => {
  const positions = dxfFacePositions(text);
  if (positions.length === 0) return null;
  const origin = firstPoint(positions);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(localPositions(positions, origin), 3));
  return { geometry, origin };
};

/**
 * Float32 positions step by 1 m at a UTM northing, so vertices are moved next to the origin in float64 before
 * they are stored. Place the result at `origin`.
 */
export const parseGeometryNearOrigin = (text: string, sourceName: string): LocalGeometry | null => {
  if (getGeometrySourceExtension(sourceName) === '.dxf') return parseDxfNearOrigin(text);
  const origin = firstObjVertex(text) ?? ZERO_ORIGIN;
  const geometry = parseObjGeometry(translateObjVertices(text, origin));
  return geometry ? { geometry, origin } : null;
};

export interface LocalObject {
  object: THREE.Object3D;
  origin: CadOrigin;
}

/**
 * Parse an entire OBJ/DXF source, relative to `origin`, into a single Object3D containing *all* geometry — every
 * solid (Mesh) and every string/polyline (Line). Used by the tunnel profile, which renders the whole imported file
 * rather than a single solid.
 */
export const parseFullGeometryNearOrigin = (text: string, sourceName: string): LocalObject | null => {
  if (getGeometrySourceExtension(sourceName) === '.dxf') {
    const local = parseDxfNearOrigin(text);
    return local ? { object: new THREE.Mesh(local.geometry), origin: local.origin } : null;
  }
  const origin = firstObjVertex(text) ?? ZERO_ORIGIN;
  const group = new OBJLoader().parse(translateObjVertices(text, origin));
  return group.children.length > 0 ? { object: group, origin } : null;
};

export interface PlacedGeometry {
  geometry: THREE.BufferGeometry;
  /** Three.js-space position of the geometry's local origin. */
  position: THREE.Vector3;
}

export const loadGeometryFromUrl = async (url: string): Promise<PlacedGeometry | null> => {
  const response = await fetch(url);
  if (!response.ok) return null;
  const text = await response.text();

  const local = parseGeometryNearOrigin(text, url);
  if (!local) return null;
  const { geometry, origin } = local;
  geometry.applyMatrix4(CAD_TO_THREEJS_MATRIX);
  geometry.computeVertexNormals();
  return { geometry, position: new THREE.Vector3(origin.x, origin.y, origin.z).applyMatrix4(CAD_TO_THREEJS_MATRIX) };
};
